import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, IconButton, Menu, MenuItem } from '@mui/material';
import {
  Close as CloseIcon,
  Image as ImageIcon,
  MoreVert as MoreVertIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';

export interface Product {
  id: string;
  name: string;
  price: string;
  description: string;
  image: string;
  status: 'active' | 'suspended' | 'under_review' | 'draft' | 'deactivated';
  category?: string;
}

interface ProductCardProps {
  product: Product;
  onDelete?: (productId: string) => void;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  isDragging?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onDelete,
  dragHandleProps,
  isDragging = false,
}) => {
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);
  const hasImage = product.image && product.image.trim() !== '';
  const [imageSrc, setImageSrc] = useState(hasImage ? product.image : '');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const handleImageError = () => {
    if (!imageError) {
      setImageError(true);
      setImageSrc('');
    }
  };

  const handleClick = () => {
    navigate(`/products/${product.id}`);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent card click
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    handleMenuClose();
    if (onDelete) {
      onDelete(product.id);
    }
  };

  const getStatusChip = () => {
    switch (product.status) {
      case 'suspended':
        return (
          <Chip
            label="Suspended"
            size="small"
            icon={<CloseIcon sx={{ fontSize: '16px' }} />}
            sx={{
              backgroundColor: '#925b64',
              color: '#ffffff',
              fontSize: '13px',
              fontWeight: 400,
              letterSpacing: '-0.08px',
              height: '32px',
              borderRadius: '8px',
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              '& .MuiChip-icon': {
                color: '#ffffff',
                opacity: 0.6,
                fontSize: '16px',
              },
              '& .MuiChip-label': {
                px: 1.5,
              },
            }}
          />
        );
      case 'under_review':
        return (
          <Chip
            label="Under review"
            size="small"
            icon={<CloseIcon sx={{ fontSize: '16px' }} />}
            sx={{
              backgroundColor: '#ecbe96',
              color: '#ffffff',
              fontSize: '13px',
              fontWeight: 400,
              letterSpacing: '-0.08px',
              height: '32px',
              borderRadius: '8px',
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              '& .MuiChip-icon': {
                color: '#ffffff',
                opacity: 0.6,
                fontSize: '16px',
              },
              '& .MuiChip-label': {
                px: 1.5,
              },
            }}
          />
        );
      case 'draft':
        return (
          <Chip
            label="Draft"
            size="small"
            sx={{
              backgroundColor: '#f1f4f4',
              color: '#181818',
              fontSize: '13px',
              fontWeight: 400,
              letterSpacing: '-0.08px',
              height: '32px',
              borderRadius: '8px',
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              '& .MuiChip-label': {
                px: 1.5,
              },
            }}
          />
        );
      case 'deactivated':
        return (
          <Chip
            label="Deactivated"
            size="small"
            icon={<CloseIcon sx={{ fontSize: '16px' }} />}
            sx={{
              backgroundColor: '#d32f2f',
              color: '#ffffff',
              fontSize: '13px',
              fontWeight: 400,
              letterSpacing: '-0.08px',
              height: '32px',
              borderRadius: '8px',
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              '& .MuiChip-icon': {
                color: '#ffffff',
                opacity: 0.6,
                fontSize: '16px',
              },
              '& .MuiChip-label': {
                px: 1.5,
              },
            }}
          />
        );
      default:
        return null;
    }
  };

  const isInactive = product.status !== 'active';

  return (
    <Box 
      onClick={handleClick}
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 0.5,
        width: '100%',
        cursor: 'pointer',
        position: 'relative',
        opacity: isDragging ? 0.85 : 1,
        '&:hover': {
          opacity: isDragging ? 0.85 : 0.8,
        },
      }}
    >
      {/* Drag handle — only starts a drag; does not navigate */}
      {dragHandleProps && (
        <Box
          {...dragHandleProps}
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
            },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 20, color: '#93989c' }} />
        </Box>
      )}

      {/* Menu Button */}
      {onDelete && (
        <IconButton
          onClick={handleMenuOpen}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            width: '32px',
            height: '32px',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
            },
          }}
        >
          <MoreVertIcon sx={{ fontSize: '20px', color: '#181818' }} />
        </IconButton>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            mt: 1,
            minWidth: 150,
            fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
          },
        }}
      >
        <MenuItem
          onClick={handleDeleteClick}
          sx={{
            fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '16px',
            color: '#d32f2f',
            '&:hover': {
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
            },
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Image Area */}
      <Box sx={{ 
        position: 'relative',
        width: '100%',
        aspectRatio: '166.5/166',
        borderRadius: '16px',
        overflow: 'hidden',
        backgroundColor: '#f1f4f4', // Background color for placeholder
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {imageSrc && !imageError ? (
          <Box
            component="img"
            src={imageSrc}
            alt={product.name}
            onError={handleImageError}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '16px',
              mixBlendMode: 'multiply',
              opacity: isInactive ? 0.5 : 1,
            }}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              opacity: isInactive ? 0.5 : 0.4,
            }}
          >
            <ImageIcon 
              sx={{ 
                fontSize: 48, 
                color: '#93989c',
              }} 
            />
            <Typography
              sx={{
                fontSize: 12,
                color: '#93989c',
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              }}
            >
              No image
            </Typography>
          </Box>
        )}
        
        {/* Status Tag */}
        {product.status !== 'active' && (
          <Box sx={{ 
            position: 'absolute', 
            top: 8, 
            left: dragHandleProps ? 48 : 8,
          }}>
            {getStatusChip()}
          </Box>
        )}
      </Box>

      {/* Service Name */}
      <Typography 
        sx={{ 
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
          fontSize: '14px',
          fontWeight: 700,
          lineHeight: 1.4,
          color: '#181818',
          letterSpacing: '-0.07px',
          margin: 0
        }}
      >
        {product.name}
      </Typography>

      {/* Price */}
      <Typography 
        sx={{ 
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
          fontSize: '14px',
          fontWeight: 700,
          lineHeight: 1.4,
          color: '#67314f',
          letterSpacing: '-0.07px',
          margin: 0
        }}
      >
        {product.price}
      </Typography>

      {/* Description */}
      <Typography 
        sx={{ 
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: 1.4,
          color: '#93989c',
          letterSpacing: '-0.0862px',
          margin: 0
        }}
      >
        {product.description}
      </Typography>
    </Box>
  );
};

export default ProductCard;
